<template>
  <!-- Loader -->
  <progress-graphic elementId="listings-progress" v-if="loading" />

  <!-- Listings -->
  <div v-else class="listings">
    <HappinessGuaranteeBanner v-if="loveWhereYouLiveEnabled && isRoomsOffering" />
    <!-- Layout Grid -->
    <div :class="`listings__layout ${mobileViewState}`">
      <!-- Error -->
      <b-row v-if="error" class="no-results-message mt-5">
        <b-col class="text-center">
          <img class="my-5" src="https://assets.bungalow.com/logos/bungalow_symbol_dark.svg" alt />
          <span>
            <div class="header-lg mb-4">An error has occurred</div>
            <p>Please refresh the page or try again in a few minues!</p>
          </span>
        </b-col>
      </b-row>

      <!-- Main Content -->
      <template v-else>
        <!-- Left Pane -->
        <section v-if="(mobileViewState && isMobile) || !isMobile">
          <div class="listings__layout-left">
            <!-- Listings -->
            <template>
              <b-row class="listings__title">
                <b-col>
                  <!-- Dynamic Header Content -->
                  <div v-if="marketSEOCategorySlug" class="mb-3" v-limit="700">
                    <h1 data-cy="listings-title" class="mt-2 pt-2 mb-3 accent-font font-size-26">
                      {{ marketCategoryHeader.headline }}
                    </h1>
                    <p data-cy="listings-category-text" class="font-base mt-3 mb-2 line-height-22">
                      {{ marketCategoryHeader.subtext }}
                    </p>
                  </div>

                  <!-- TEST: SEO Test -->
                  <h1 v-else-if="seoTestHeader" class="mt-2 pt-2 mb-3 header-md">
                    {{ seoTestHeader }}
                  </h1>

                  <!-- Default Header-->
                  <h1 v-else class="mt-2 pt-2 mb-3 header-md" data-cy="listings-title">
                    {{ listingHeader }} {{ marketAreaDisplayName }}
                  </h1>

                  <div
                    v-if="listingsReady && totalPropertyCount"
                    class="lead mt-2"
                    data-cy="listings-property-count"
                  >
                    <template v-if="somePropertiesFiltered">
                      Showing {{ filteredPropertyCount }} of {{ totalPropertyCount }}
                      {{ roomsOrHomes }}
                      available now
                    </template>
                    <template v-else>
                      {{ totalPropertyCount }} result{{ totalPropertyCount === 1 ? '' : 's' }}
                      for
                      {{ market.displayName }}
                    </template>
                  </div>
                </b-col>
                <b-col cols="12" class="mt-4">
                  <ListingsFilters class="desktop-filter-set" :homeType="homeType" />
                </b-col>
              </b-row>

              <!-- No Properties -->
              <b-row
                v-if="propertyListEmpty || !filteredPropertyCount"
                class="no-results-message"
                :class="{
                  active: propertyListEmpty || !filteredPropertyCount,
                }"
              >
                <b-col class="text-center">
                  <!-- All Filtered -->
                  <div v-if="totalPropertyCount && !filteredPropertyCount" class="mb-5">
                    <img
                      class="my-4 mt-md-5"
                      src="https://assets.bungalow.com/illustrations/full-color/happy-home.png"
                    />
                    <div class="header-md mb-4">
                      {{ isRoomsOffering ? 'No Shared Homes Found' : 'No Entire Homes Found' }}
                    </div>
                    <p v-limit="350" class="mx-auto font-large">
                      There are no matches for the filters you currently have set. Let's broaden the
                      search!
                    </p>
                    <b-button
                      v-limit="250"
                      class="mx-auto font-weight-bold mt-4"
                      variant="dark"
                      @click="clearFilters"
                    >
                      Clear Filters
                    </b-button>
                  </div>

                  <hr class="my-4" />

                  <!-- No Properties Available -->
                  <NoResults
                    v-if="propertyListEmpty && comingSoonListEmpty"
                    :market="market"
                    :isMobile="isMobile"
                    :homeType="roomsOrHomes"
                    :marketCategoryName="marketCategoryDisplayName"
                  />
                </b-col>
              </b-row>
              <!-- Property List -->
              <div class="mb-5 listings__cards">
                <template v-for="(property, index) in listViewProperties">
                  <!-- Promo Insert -->
                  <div
                    v-if="showPromoInsert && index === promoInsertPosition"
                    :key="`promo-insert-${index}`"
                    class="promo-listings-insert"
                  >
                    <img
                      src="https://assets.bungalow.com/illustrations/live-action/piggy-bank.jpg"
                    />
                    <div class="d-flex flex-column">
                      <h4 class="header-md mb-2" v-html="promoInsertHeader" />
                      <p class="mb-0 font-small" v-html="promoInsertText" />
                    </div>
                  </div>

                  <!-- Waitlist Signup -->
                  <ListingsWaitlistInsert
                    v-if="index === waitlistInsertPosition"
                    :key="`waitlist-insert-${index}`"
                    :market="market"
                    :onCTAClick="openWaitListModal"
                  />

                  <!-- Listing Card -->
                  <div
                    class="listings__card"
                    @mouseenter="propertyMousedOver(property)"
                    @mouseleave="propertyMousedOut(property)"
                    :key="`property-list_${property.slug}_${index}`"
                    @click="onListingCards(property, property._recentlyViewed)"
                  >
                    <router-link
                      :to="{
                        name: 'Property',
                        params: {
                          id: property.id,
                          slug: property.slug,
                          market: property.market.slug,
                        },
                        query: $route.query,
                      }"
                    >
                      <StaffContextMenuOverride :property="property">
                        <property-card
                          :hide-roommates="!interestFilterActive"
                          hide-actions
                          clickable
                          :property="property"
                          :recentlyViewed="property._recentlyViewed"
                        />
                      </StaffContextMenuOverride>
                    </router-link>
                  </div>
                </template>

                <!-- NOTE: This empty card ensures no orphaned cards for 2/3 full rows -->
                <div class="listings__card" v-if="!isMobile" />

                <ListingsWaitlistInsert
                  v-if="listViewProperties.length && listViewProperties.length < 4"
                  :market="market"
                  :onCTAClick="openWaitListModal"
                />

                <!-- Cross Sell Properties -->
                <ListingsCrossSell
                  v-if="crossSellListings.length"
                  :market="market"
                  :crossSellListings="crossSellListingsToShow"
                  :isRoomsOffering="isRoomsOffering"
                  :onCrossSellClick="onCrossSellClick"
                />
              </div>

              <template v-if="!comingSoonListEmpty">
                <b-row class="listings__title">
                  <b-col>
                    <h2 class="mb-4 pb-3 header-md" v-if="market">
                      Coming soon in
                      <strong class="text-capitalize">{{ market.displayName }}</strong>
                    </h2>
                  </b-col>
                </b-row>

                <div class="listings__cards">
                  <div
                    class="listings__card"
                    v-for="(csProperty, index) in filteredUpcomingProperties"
                    @mouseover="propertyMousedOver(csProperty)"
                    @mouseout="propertyMousedOut(csProperty)"
                    :key="`property-list_${csProperty.slug}_${index}`"
                    @click="onListingCards(csProperty)"
                  >
                    <router-link
                      :to="{
                        name: 'Property',
                        params: {
                          id: csProperty.id,
                          slug: csProperty.slug,
                          market: csProperty.market.slug,
                        },
                        query: $route.query,
                      }"
                    >
                      <property-card
                        :hide-roommates="!interestFilterActive"
                        hide-actions
                        clickable
                        :property="csProperty"
                      />
                    </router-link>
                  </div>
                </div>

                <b-col class="w-100 py-3 new-listing-wrapper" cols="12" v-if="isMobile">
                  <b-button
                    @click="openWaitListModal"
                    class="d-inline w-100 d-none font-weight-bold new-listing-btn"
                    variant="primary"
                    v-if="market"
                  >
                    Get New Listings
                    <inline-svg :src="require('@/assets/icons/fnd/mail.svg')" />
                  </b-button>
                </b-col>
              </template>
            </template>
          </div>

          <!-- SEO Content -->
          <div class="seo-content">
            <div v-if="!marketSEOCategorySlug" class="cream--bg listings__layout-left">
              <SEOContentBlock :market="market.displayName" :homeType="homeType" />
            </div>
            <div class="listings__layout-left">
              <SEOCategoryLinks :market="market" :isRoomsOffering="isRoomsOffering" />
            </div>
          </div>
        </section>

        <!-- Right Pane -->
        <section
          v-if="(listingsReady && !mobileViewState && isMobile) || !isMobile"
          class="listings__layout-right"
        >
          <listings-map
            v-if="renderListingsMap"
            :market="market"
            :isMobile="isMobile"
            :properties="mapProperties"
            :property-hovered="propertyHovered"
            :interestFilterActive="interestFilterActive"
            :setVisibleMapProperties="setVisibleMapProperties"
          />
          <div class="map-no-results p-4 h-100" v-else-if="isMobile">
            <div class="header-lg mb-4">
              {{ isRoomsOffering ? 'No Shared Homes Found' : 'No Entire Homes Found' }}
            </div>
            <p v-limit="350" class="mx-auto font-large">
              There are no matches for the filters you currently have set. Let's broaden the search!
            </p>
            <b-button class="wide font-weight-bold mt-5" variant="primary" @click="clearFilters">
              Clear Filters
            </b-button>
          </div>
        </section>
      </template>

      <!-- Mobile Nav -->
      <div v-if="!error" class="mobile-toggle-container" ref="mobileToggleContainer">
        <div v-if="!mapboxCapable" class="map-list-toggle d-md-none text-center">
          <div class="mobile-icon-wrapper">
            <img :src="getIconUrl('list.svg')" alt />
            <span class="label font-base">List</span>
          </div>
        </div>
        <div v-else class="map-list-toggle d-md-none text-center" @click="onMapViewToggle()">
          <div class="mobile-icon-wrapper" v-if="mobileViewState">
            <img :src="getIconUrl('mappin.svg')" alt />
            <span class="label font-base">Map view</span>
          </div>
          <div class="mobile-icon-wrapper" v-if="!mobileViewState">
            <img :src="getIconUrl('list.svg')" alt />
            <span class="label font-base">List view</span>
          </div>
        </div>
        <div class="filter-toggle d-md-none text-center" @click="openMobileFilters">
          <div class="mobile-icon-wrapper">
            <img :src="getIconUrl('filters.svg')" alt />
            <span class="label font-base">All filters</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { startCase, orderBy } from 'lodash-es'
import { schema, generateListingsSchema, generateBreadcrumbSchema } from '@/schema'
import { homeTypes } from '@/constants/nav.js'
import * as ListingService from '@/constants/listingsService.js'
import { PROMO_BANNER } from '@/constants/listings.js'
import { importMapboxGeocoder, importBaseMapbox } from '@/utils/scripts.js'

// TEST: SEO Test
import { SEO_TEST_PATHS } from '@/constants/seoContent'

import ProgressGraphic from '@/components/Layout/ProgressGraphic.vue'
import HappinessGuaranteeBanner from '@/components/Banners/HappinessGuaranteeBanner.vue'
import ListingsMap from '@/components/Listings/ListingsMap.vue'
import ListingsFilters from '@/components/Listings/ListingsFilters.vue'
import ListingsWaitlistInsert from '@/components/Listings/ListingsWaitlistInsert.vue'
import ListingsCrossSell from '@/components/Listings/ListingsCrossSell.vue'
import NoResults from '@/components/Listings/NoResults.vue'
import SEOContentBlock from '@/components/Contentful/Listings/SEOContentBlock.vue'
import SEOCategoryLinks from '@/components/Contentful/Listings/SEOCategoryLinks.vue'

import ListingFiltersModal from '../../components/Modals/Listings/ListingFiltersModal.vue'
import WaitListModal from '../../components/Modals/Listings/WaitListModal.vue'

// NOTE: Check the README.md for more details on programamtic SEO content

export default {
  name: 'Listings',
  components: {
    ProgressGraphic,
    ListingsFilters,
    ListingsMap,
    HappinessGuaranteeBanner,
    NoResults,
    SEOContentBlock,
    SEOCategoryLinks,
    ListingsWaitlistInsert,
    ListingsCrossSell,
  },
  metaInfo() {
    const { title, description } = this.metaSEOContent

    return {
      title,
      meta: [
        {
          vmid: 'description',
          property: 'description',
          name: 'description',
          content: description,
        },
        {
          vmid: 'og:title',
          property: 'og:title',
          name: 'og:title',
          content: title,
        },
        {
          vmid: 'og:description',
          property: 'og:description',
          name: 'og:description',
          content: description,
        },
        {
          vmid: 'og:image',
          property: 'og:image',
          name: 'og:image',
          content: this.marketShareImage,
        },
      ],
      link: [
        {
          rel: 'canonical',
          // Remove trailing slash from pathname on canonical link to avoid duplicate SEO content
          href: `https://bungalow.com${window.location.pathname.replace(/\/$/, '')}`,
        },
      ],
      script: this.structuredData,
    }
  },
  props: {
    homeType: {
      type: String,
      required: true,
      validator: (value) => !!homeTypes.find(({ key }) => key === value),
    },
  },
  data() {
    const listView = sessionStorage.getItem('listView')
    return {
      loading: false,
      mapboxCapable: false,
      mapboxJSLoaded: false,
      mapboxCSSLoaded: false,
      mapboxGEOLoaded: false,
      listingHeader: 'Available now ',
      roomsOrHomes: 'rooms',
      error: false,
      listingsScrollPosition: 0,
      visibleMapProperties: null,
      propertyHovered: null,
      mobileViewState: !listView || listView === 'true', // true = List, false = map
      showPromoInsert: false,
      recentlyViewedListings: [],
    }
  },
  created() {
    this.importMapbox()
    this.recentlyViewedListings = this.getRecentlyViewedListingsByMarket(this.marketSlug)
  },
  mounted() {
    window.addEventListener('storage', this.setRecentlyViewedListings)
  },
  destroyed() {
    window.removeEventListener('storage', this.setRecentlyViewedListings)
  },
  watch: {
    homeType: {
      handler(val) {
        val && this.saveHomeType(val)
        if (val === 'group_living') {
          this.listingHeader = 'Homes for rent '
          this.roomsOrHomes = 'homes'
        } else {
          this.roomsOrHomes = 'rooms'
          this.listingHeader = 'Rooms for rent '
        }
      },
      immediate: true,
    },
    $route: {
      async handler(to, from) {
        const { offering, market, category } = to.params
        const {
          offering: prevOffering,
          market: prevMarket,
          category: prevCategory,
        } = from?.params || {}

        const marketChange = market !== prevMarket
        const offeringChange = offering !== prevOffering
        const categoryChange = category !== prevCategory

        if (offeringChange || marketChange || categoryChange) {
          this.loading = true

          // TODO: from Q2/2022 - refactor this to be API driven:
          // Pricing constants are required for listings, fetch first!
          if (!this.leaseLengthMultipliers)
            await this.fetchConstants({ category: 'Lease Length Premiums' })

          await Promise.all([
            // Establish start time for loading
            this.getListings(),
            // market has changed fetch details
            marketChange && this.fetchMarket(market),
          ])

          // Market not found, bounce to home
          if (!Object.keys(this.market).length)
            return this.$router
              .push({
                path: '/',
                query: {
                  ...this.$route.query,
                  'find-a-home': true,
                },
              })
              .catch(() => {})

          // Lazily fetch x-sell listings:
          this.fetchCrossSellListings({
            marketingType: this.crossSellHomeType,
            marketSlug: this.marketSlug,
          })

          // track the length of time the user waits for results
          this.loading = false
        }
      },
      immediate: true,
    },
    mapboxLoaded(mapboxLoaded) {
      if (window.mapboxgl && window.mapboxgl.supported) {
        this.mapboxCapable = window.mapboxgl.supported()
      }
    },
  },
  computed: {
    ...mapGetters(['isMobile', 'isTablet', 'isLaptop']),
    ...mapGetters('constants', ['leaseLengthMultipliers']),
    ...mapGetters('interests', ['interests']),
    ...mapGetters('markets', ['market']),
    ...mapGetters('flags', ['featureFlags']),
    ...mapGetters('listings', [
      'listingsFetched',
      'upcomingListingsFetched',
      'recentlyViewedStorageKey',
      'getRecentlyViewedListingsByMarket',
      'crossSellListings',
    ]),
    ...mapGetters('nav', {
      savedHomeType: 'homeType',
    }),
    ...mapGetters('listings', ['listings', 'upcomingListings']),
    // TEST: SEO Test
    seoTestHeader() {
      return SEO_TEST_PATHS[this.$route.path]?.headline
    },
    metaSEOContent() {
      // Use the default until market loaded
      if (!this.market?.displayName) return { title: null, description: null }

      // Build reusable string chunks...
      const { displayName, regionCode } = this.market
      const marketChunk = `${displayName}${regionCode ? ', ' + regionCode : ''}`
      const countChunk = this.isRoomsOffering ? 'top' : `${this.totalPropertyCount} top`
      const typeChunk = this.isRoomsOffering ? 'Rooms' : 'Homes and Apartments'
      const sharedChunk = this.isRoomsOffering ? '| Shared Houses & Apartments ' : ''
      const valuePropChunk = `Bungalow offers beautiful shared living spaces, accessible monthly rent and a streamlined renters experience.`
      const hasPriceSymbol = this.marketSEOCategorySlug?.includes('$') ? '$' : ''

      // Default title/description
      let title = `${typeChunk} for Rent in ${marketChunk} ${sharedChunk}| Bungalow`
      let description = this.isRoomsOffering
        ? `Looking to rent a room in ${marketChunk}? Explore co-living in beautiful shared homes with flexible leasing and vetted roommates.`
        : `Looking to rent a house or apartment in ${marketChunk}? Here are the latest available ${marketChunk} homes for rent. Beautiful homes in incredible locations, with pricing that makes sense.`

      // TEST: SEO Test
      if (SEO_TEST_PATHS[this.$route.path]?.metaTitle) {
        title = SEO_TEST_PATHS[this.$route.path]?.metaTitle
      }

      const category = hasPriceSymbol // Retains the $ if present in the URL
        ? startCase(this.marketSEOCategorySlug).split(' ').join(' $')
        : startCase(this.marketSEOCategorySlug)

      // Build a dynamic page title based on SEO/URL category:
      switch (this.marketSEOCategoryKey) {
        case 'neighborhood':
        case 'neighborhoods':
          title = `${typeChunk} for Rent ${category} | ${category} House Share, ${marketChunk} Near Me`
          description = `Find ${countChunk} available ${typeChunk.toLowerCase()} for rent in ${category}, ${marketChunk}. ${valuePropChunk}`
          break
        case 'zip':
          title = `${category} Zip ${typeChunk} for Rent | ${marketChunk} | Bungalow`
          description = `Find ${countChunk} available ${typeChunk.toLowerCase()} for rent in the zip ${category} in ${marketChunk}. ${valuePropChunk}`
          break
        case 'schools':
          title = `${category} Off-Campus ${typeChunk} for Rent | ${marketChunk} | Bungalow`
          description = `Find ${countChunk} available off-campus ${typeChunk.toLowerCase()} for rent near ${category} in ${marketChunk}. ${valuePropChunk}`
          break
        case 'landmarks':
          title = `${typeChunk} for Rent Near ${category} | ${marketChunk} | Bungalow`
          description = `Find ${countChunk} available ${typeChunk.toLowerCase()} for rent near ${category} in ${marketChunk}. ${valuePropChunk}`
          break
        case 'features':
          if (hasPriceSymbol) {
            title = `${typeChunk} for Rent ${category} | ${marketChunk} | Bungalow`
            description = `Find ${countChunk} available ${typeChunk.toLowerCase()} for rent ${category} in ${marketChunk}. ${valuePropChunk}`
          } else {
            title = `${category} ${typeChunk} for Rent | ${marketChunk} | Bungalow`
            description = `Find ${countChunk} available ${category.toLowerCase()} ${typeChunk.toLowerCase()} for rent in ${marketChunk}. ${valuePropChunk}`
          }
          break
      }

      return { title, description }
    },
    marketCategoryHeader() {
      // Build reusable chunks to build dynamic titles
      const { displayName, regionCode } = this.market
      const marketChunk = `${displayName}${regionCode ? ', ' + regionCode : ''}`
      const typeChunk = this.isRoomsOffering ? 'Rooms' : 'Homes and Apartments'
      const valuePropChunk = `Bungalow offers beautiful shared living spaces, accessible monthly rent and a streamlined renters experience.`
      const hasPriceSymbol = this.marketSEOCategorySlug?.includes('$') ? '$' : ''

      let headline = `${typeChunk} for Rent in ${displayName}`
      let subtext = this.isRoomsOffering
        ? `Looking to rent a room in ${marketChunk}? Explore co-living in beautiful shared homes with flexible leasing and vetted roommates.`
        : `Looking to rent a house or apartment in ${marketChunk}? Here are the latest available ${marketChunk} homes for rent. Beautiful homes in incredible locations, with pricing that makes sense.`

      const category = hasPriceSymbol // Retains the $ if present in the URL
        ? startCase(this.marketSEOCategorySlug).split(' ').join(' $')
        : startCase(this.marketSEOCategorySlug)

      // Builds dynamic page header and subtext based on URL/SEO...
      switch (this.marketSEOCategoryKey) {
        case 'neighborhood':
        case 'neighborhoods':
          headline = `${typeChunk} for Rent ${category}, ${marketChunk}`
          subtext = `Find top ${typeChunk.toLowerCase()} for rent in ${category}. ${valuePropChunk}`
          break
        case 'schools':
          headline = `${category} Off-Campus Student ${typeChunk} for Rent in ${marketChunk}`
          subtext = `Find top off-campus ${typeChunk.toLowerCase()} for rent near ${category} in ${marketChunk}. ${valuePropChunk}`
          break
        case 'landmarks':
          headline = `${typeChunk} for Rent Near ${category} in ${marketChunk}`
          subtext = `Find top ${typeChunk.toLowerCase()} for rent near ${category} in ${marketChunk}. ${valuePropChunk}`
          break
        case 'zip':
          headline = `${category} Zip ${typeChunk} for Rent in ${marketChunk}`
          subtext = `Find top ${typeChunk.toLowerCase()} for rent in the ${category} zip in ${marketChunk}. ${valuePropChunk}`
          break
        case 'features':
          if (hasPriceSymbol) {
            headline = `${typeChunk} for Rent ${category} in ${marketChunk}`
            subtext = `Find top ${typeChunk.toLowerCase()} for rent ${category} in ${marketChunk}. ${valuePropChunk}`
          } else {
            headline = `${category} Homes and Apartments for Rent in ${marketChunk}`
            subtext = `Find top available ${category.toLowerCase()} for rent ${typeChunk.toLowerCase()} in ${marketChunk}. ${valuePropChunk}`
          }
          break
      }

      // TEST: SEO Test
      if (SEO_TEST_PATHS[this.$route.path]?.headline) {
        headline = SEO_TEST_PATHS[this.$route.path].headline
      }

      return { headline, subtext }
    },
    loveWhereYouLiveEnabled() {
      return this.featureFlags.includes('love_where_you_live')
    },
    isRoomsOffering() {
      return this.homeType !== 'group_living'
    },
    marketSlug() {
      return this.$route.params.market
    },
    marketSEOCategoryKey() {
      return this.$route?.meta?.category
    },
    marketSEOCategorySlug() {
      return this.$route?.params?.category
    },
    marketCategoryDisplayName() {
      const category = this.$route?.params?.category
      return category ? startCase(category) : ''
    },
    marketAreaDisplayName() {
      return this.marketSEOCategorySlug
        ? `near ${this.marketCategoryDisplayName}`
        : `in ${this.market.displayName}`
    },
    interestFilterActive() {
      return !!this.$route.query.roommateInterests
    },
    filteredProperties() {
      return ListingService.getFilteredProperties(this.$route.query, this.listings)
    },
    filteredUpcomingProperties() {
      return ListingService.getFilteredProperties(this.$route.query, this.upcomingListings)
    },
    structuredData() {
      if (!this.listingsFetched) return []

      // Generate dynamic structured data:
      schema.listings = generateListingsSchema(this.listings)
      schema.breadcrumbs = generateBreadcrumbSchema(this.$route)

      // TEST: SEO Test
      if (SEO_TEST_PATHS[this.$route.path]?.schema) {
        schema.listings[0] = {
          ...schema.listings[0],
          ...SEO_TEST_PATHS[this.$route.path]?.schema,
        }
      }

      // Schema manifest for this page
      const schemaList = ['listings', 'organization', 'breadcrumbs']

      // Build structured data by merging all of the schemas...
      const structuredData = schemaList.reduce((data, key) => data.concat(schema[key]), [])

      // Inject script
      return structuredData.map((data, index) => {
        return {
          id: `structured-data-${index}`,
          type: 'application/ld+json',
          json: data,
        }
      })
    },
    waitlistInsertPosition() {
      const { innerWidth } = window
      if (innerWidth <= 1200) return 5

      return innerWidth <= 1400 ? 13 : 12
    },
    promoInsertPosition() {
      const { innerWidth } = window
      if (innerWidth <= 1200) return 5

      return innerWidth <= 1400 ? 13 : 12
    },
    crossSellHomeType() {
      return this.isRoomsOffering ? 'group_living' : 'co_living'
    },
    crossSellListingsToShow() {
      if (this.isMobile || this.isTablet) return [this.crossSellListings[0]]

      return this.isLaptop ? this.crossSellListings.slice(0, 2) : this.crossSellListings
    },
    promoInsertHeader() {
      return PROMO_BANNER[this.homeType].title
    },
    promoInsertText() {
      return PROMO_BANNER[this.homeType].description
    },
    marketShareImage() {
      return this.market.imgMd || 'https://assets.bungalow.com/images/meta/bungalow_social.jpg'
    },
    mapboxLoaded() {
      return this.mapboxJSLoaded && this.mapboxCSSLoaded && this.mapboxGEOLoaded
    },
    listingsReady() {
      return this.listingsFetched
    },
    renderListingsMap() {
      return this.mapboxLoaded && this.mapboxCapable && this.mapProperties.length
    },
    propertyListEmpty() {
      return this.listingsFetched && !this.listings.length
    },
    comingSoonListEmpty() {
      return this.upcomingListingsFetched && !this.upcomingListings.length
    },
    mapProperties() {
      return [...this.filteredProperties, ...this.filteredUpcomingProperties]
    },
    somePropertiesFiltered() {
      return this.totalPropertyCount > this.filteredPropertyCount
    },
    totalPropertyCount() {
      return [...this.listings, ...this.upcomingListings].length
    },
    totalB2BPropertyCount() {
      return [...this.listings, ...this.upcomingListings].filter((home) => !home.retailProperty)
        .length
    },
    filteredPropertyCount() {
      return [...this.filteredProperties, ...this.filteredUpcomingProperties].length
    },
    listViewProperties() {
      // On initial mount map code has not run so show the default list
      const propertiesToShow = !this.visibleMapProperties
        ? this.filteredProperties
        : // Filters properties, based on what's visible on the map
          [...this.filteredProperties].filter(({ slug }) =>
            this.visibleMapProperties.includes(slug)
          )
      // Add recently viewed flag
      const annotatedProperties = propertiesToShow.map((property) => ({
        ...property,
        _recentlyViewed: !!this.recentlyViewedListings.includes(property.slug),
      }))
      return orderBy(annotatedProperties, ['isFeatured'], ['desc'])
    },
  },
  methods: {
    ...mapActions('markets', ['fetchMarket', 'fetchMarkets']),
    ...mapActions('listings', ['fetchListings', 'fetchCrossSellListings']),
    ...mapActions('constants', ['fetchConstants']),
    ...mapActions('nav', ['saveHomeType']),
    setRecentlyViewedListings(event = {}) {
      // Only trigger off the right storage update(s):
      if (event.key === this.recentlyViewedStorageKey) {
        this.recentlyViewedListings = this.getRecentlyViewedListingsByMarket(this.marketSlug)
      }
    },
    onCrossSellClick() {
      this.$segment.track('MLP Cross Sell Click', {
        market: this.market.displayName,
        homeType: this.crossSellHomeType,
      })

      const offering = homeTypes.find(({ key }) => key === this.crossSellHomeType).route
      this.$router
        .push({
          name: 'Listings',
          params: { offering },
          query: this.$route.query,
        })
        .catch(() => {})
    },
    openMobileFilters() {
      this.$root.$emit('openModal', {
        modalComponent: ListingFiltersModal,
        fullscreen: true,
        props: {
          homeType: this.homeType,
        },
      })
    },
    /**
     * properties: Array of property slugs
     *
     * this is used to only show the properties visible in the map
     * on the left side list view improving the overall experience
     */
    setVisibleMapProperties(properties) {
      this.visibleMapProperties = properties
    },
    importMapbox() {
      importBaseMapbox(
        () => {
          this.mapboxJSLoaded = true
        },
        () => {
          this.mapboxCSSLoaded = true
        }
      )
      importMapboxGeocoder(() => (this.mapboxGEOLoaded = true))
    },
    openWaitListModal() {
      this.$root.$emit('openModal', {
        modalComponent: WaitListModal,
        props: {
          market: this.market,
          homeType: this.homeType,
        },
      })
    },
    getIconUrl(icon) {
      return require('@/assets/icons/' + icon)
    },
    propertyMousedOver(property) {
      this.propertyHovered = property
    },
    propertyMousedOut() {
      this.propertyHovered = null
    },
    async getListings() {
      // reset visible map properties when market changes
      this.visibleMapProperties = null

      // Fetch all possible listings...
      const startDateTime = new Date()

      await this.fetchListings({
        marketingType: this.homeType,
        marketSlug: this.marketSlug,
      })

      const waitTimeSeconds = Math.ceil((new Date().getTime() - startDateTime.getTime()) / 1000)

      this.$segment.track('MLP Loaded', {
        market: this.marketSlug,
        marketingType: this.homeType,
        category: this.marketSEOCategoryKey,
        properties: this.totalPropertyCount,
        B2Bproperties: this.totalB2BPropertyCount,
        waitTimeSeconds,
      })
    },
    clearFilters() {
      this.$router.replace({ query: {} }).catch(() => {})

      this.$segment.track('Listings Filter Updated', {
        type: 'clear',
        market: this.$route.params.market,
      })
    },
    onMapViewToggle() {
      this.mobileViewState = !this.mobileViewState
      if (this.mobileViewState) {
        setTimeout(() => {
          window.scrollTo(0, this.listingsScrollPosition)
        }, 25)
      }

      if (this.mobileViewState === false) {
        this.$segment.track('Listings Mobile View Toggles', {
          action: 'Toggle Map',
          label: 'Toggle Map',
          value: 'Map',
        })
        sessionStorage.setItem('listView', false)
      } else {
        this.$segment.track('Listings Mobile View Toggles', {
          action: 'Toggle Map',
          label: 'Toggle List',
          value: 'List',
        })
        sessionStorage.setItem('listView', true)
      }
    },
    onListingCards(property, isRecentlyViewed) {
      const marketingType =
        property.propertyMarketingType === 'co_living' ? 'rooms-for-rent' : 'homes-for-rent'
      const propertyURL = `https://bungalow.com/${marketingType}/${property.market.slug}/${property.slug}`
      const livingSituation = property.propertyMarketingType === 'co_living' ? 'coliving' : 'groups'

      this.$segment.track('List Listing Card Click', {
        category: 'Listings',
        label: 'Clicked Listing Card',
        value: property.id,
        recentlyViewed: !!isRecentlyViewed,
        total_bathrooms: property.num_bathrooms,
        total_bedrooms: property.total_room_count,
        property_image: property.images[0]?.mdUrl,
        property_url: propertyURL,
        listing_name: property.headline,
        available_rooms: property.availableRoomCount,
        property_marketing_type: livingSituation,
        market: property.market.display_name,
      })
    },
  },
}
</script>

<style lang="scss">
@import '@/styles/listings.scss';

.listings {
  .desktop-filter-set {
    @include tabletAndDown {
      display: none;
    }
  }
  .mobile-toggle-container {
    @include desktop {
      display: none;
    }
  }
  .listings__layout.true {
    .listings__layout-right {
      @include mobile {
        display: none;
      }
    }
  }
}
</style>
